import React, { lazy, type ComponentType } from "react";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const CHUNK_ERROR_RELOAD_KEY = "aac-chunk-reload-attempt";

const canUseSessionStorage = (): boolean =>
  typeof window !== "undefined" && typeof sessionStorage !== "undefined";

const clearReloadAttempt = (): void => {
  if (!canUseSessionStorage()) return;
  sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
};

const hasReloadAttempt = (): boolean => {
  if (!canUseSessionStorage()) return false;
  return sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY) === "1";
};

const markReloadAttempt = (): void => {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, "1");
};

export const lazyWithRetryInternals = {
  clearReloadAttempt,
  hasReloadAttempt,
  markReloadAttempt,
  reloadPage: (): void => {
    if (typeof window === "undefined") return;
    window.location.reload();
  },
};

const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk")
  );
};

export async function loadWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  retries = MAX_RETRIES,
): Promise<{ default: T }> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const importedModule = await importFn();
      lazyWithRetryInternals.clearReloadAttempt();
      return importedModule;
    } catch (error) {
      lastError = error;
      if (attempt < retries && isChunkLoadError(error)) {
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY_MS * attempt),
        );
        continue;
      }

      // Po retries: cache mismatch po deployi - max jedna automatická reload za session
      if (
        isChunkLoadError(error) &&
        typeof window !== "undefined" &&
        !lazyWithRetryInternals.hasReloadAttempt()
      ) {
        lazyWithRetryInternals.markReloadAttempt();
        lazyWithRetryInternals.reloadPage();
      }

      throw error;
    }
  }

  throw lastError;
}

/**
 * Lazy load component with retry on chunk load failure.
 * Fixes Sentry errors: "Failed to fetch dynamically imported module",
 * "Importing a module script failed" - often caused by cache mismatch after deployment.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  retries = MAX_RETRIES,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    loadWithRetry(importFn, retries),
  ) as React.LazyExoticComponent<T>;
}
